import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, Stack, TextField, Typography, useMediaQuery,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import MyLocationRoundedIcon from '@mui/icons-material/MyLocationRounded';
import maplibregl from 'maplibre-gl';
import { useTranslation } from 'react-i18next';
import { locationEditorMapStyle, mapStyle } from '../../mapStyle';
import { normalizeOpeningPeriods } from '../../utils/openingHours';
import { createPlaceContinuationDraft, getPlaceCategories, getPlaceContinuationDraftStatus, submitNewPlace, submitPlaceEdit } from '../../services/api';
import { findBuildingSnapPoint, loadNearbyBuildingLines } from '../../services/osmBuildings';

const CREATE_STEPS = [
  'business', 'categories', 'location', 'hours', 'contact',
];
const countWords = (value) => String(value || '').trim().split(/\s+/u).filter(Boolean).length;
const emptyDay = (day) => ({ day, ranges: [] });
const SAVED_CONTACT_KEY = 'calmpi.placeEditContact';
const loadSavedContact = () => {
  try {
    const value = JSON.parse(window.localStorage.getItem(SAVED_CONTACT_KEY) || 'null');
    if (!value || typeof value !== 'object') return null;
    return {
      firstName: String(value.firstName || ''),
      lastName: String(value.lastName || ''),
      phone: String(value.phone || ''),
      email: String(value.email || ''),
    };
  } catch {
    return null;
  }
};
const saveContact = (contact) => {
  try {
    window.localStorage.setItem(SAVED_CONTACT_KEY, JSON.stringify(contact));
  } catch {
    // Local storage can be unavailable in private/restricted browser contexts.
  }
};
const clearSavedContact = () => {
  try {
    window.localStorage.removeItem(SAVED_CONTACT_KEY);
  } catch {
    // Local storage can be unavailable in private/restricted browser contexts.
  }
};
const toHours = (openingHours) => {
  const result = Array.from({ length: 7 }, (_, day) => emptyDay(day));
  normalizeOpeningPeriods(openingHours).forEach((period) => {
    result[period.day].ranges.push({
      isOpen24Hours: period.isOpen24Hours,
      open: period.start % 1440,
      close: period.end % 1440,
    });
  });
  return result;
};
const serializeHours = (days) => days.flatMap((day) => {
  if (!day.ranges.length) {
    return [{ day: day.day, isClosed: true, isOpen24Hours: false, open: null, close: null }];
  }
  return day.ranges.map((range) => ({
    day: day.day,
    isClosed: false,
    isOpen24Hours: range.isOpen24Hours,
    open: range.isOpen24Hours ? null : range.open,
    close: range.isOpen24Hours ? null : range.close,
  }));
});
const toTime = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
const fromTime = (value) => {
  const [hour, minute] = value.split(':').map(Number);
  return hour * 60 + minute;
};
const pointsFromPlace = (place) => {
  const ring = place?.footprint?.type === 'MultiPolygon'
    ? place.footprint.coordinates?.[0]?.[0] || []
    : place?.footprint?.coordinates?.[0] || [];
  return ring.length > 1 ? ring.slice(0, -1) : [];
};
const distanceToSegment = (point, start, end) => {
  const deltaX = end.x - start.x;
  const deltaY = end.y - start.y;
  const lengthSquared = deltaX * deltaX + deltaY * deltaY;
  const projection = lengthSquared === 0
    ? 0
    : Math.max(0, Math.min(1,
      ((point.x - start.x) * deltaX + (point.y - start.y) * deltaY) / lengthSquared));
  const closestX = start.x + projection * deltaX;
  const closestY = start.y + projection * deltaY;
  return Math.hypot(point.x - closestX, point.y - closestY);
};
const addPolygonPoint = (points, point, map) => {
  if (points.length < 2) return [...points, point];
  const clickPoint = map.project(point);
  const screenPoints = points.map((current) => map.project(current));
  const segmentCount = points.length >= 3 ? points.length : points.length - 1;
  let closestSegment = -1;
  let closestDistance = Number.POSITIVE_INFINITY;

  for (let index = 0; index < segmentCount; index += 1) {
    const nextIndex = (index + 1) % points.length;
    const distance = distanceToSegment(
      clickPoint,
      screenPoints[index],
      screenPoints[nextIndex],
    );
    if (distance < closestDistance) {
      closestDistance = distance;
      closestSegment = index;
    }
  }

  if (closestSegment === -1 || closestDistance > 24) return [...points, point];
  const updated = [...points];
  updated.splice(closestSegment + 1, 0, point);
  return updated;
};

const pointInPolygon = (point, vertices) => {
  if (!point || vertices.length < 3) return false;
  let inside = false;
  for (let index = 0, previous = vertices.length - 1; index < vertices.length; previous = index++) {
    const [x, y] = vertices[index];
    const [previousX, previousY] = vertices[previous];
    if (((y > point[1]) !== (previousY > point[1])) && point[0] < ((previousX - x) * (point[1] - y)) / ((previousY - y) || Number.EPSILON) + x) inside = !inside;
  }
  return inside;
};
function GeometryPreview({ location, footprint }) {
  const nodeRef = useRef(null);
  const footprintKey = JSON.stringify(footprint || null);
  const locationKey = JSON.stringify(location || null);
  useEffect(() => {
    const previewLocation = JSON.parse(locationKey);
    const previewFootprint = JSON.parse(footprintKey);
    if (!nodeRef.current || !previewLocation) return undefined;
    const map = new maplibregl.Map({ container: nodeRef.current, style: mapStyle, center: previewLocation, zoom: 17, pitch: 20, interactive: false });
    const markerElement = document.createElement('div');
    markerElement.className = 'place-marker';
    markerElement.style.setProperty('--marker-color', '#1677ff');
    const markerPin = document.createElement('span');
    markerPin.className = 'marker-pin';
    markerElement.append(markerPin);
    const marker = new maplibregl.Marker({ element: markerElement, anchor: 'bottom' }).setLngLat(previewLocation).addTo(map);
    map.on('load', () => {
      if (!previewFootprint) return;
      map.addSource('preview-footprint', { type: 'geojson', data: { type: 'Feature', properties: {}, geometry: previewFootprint } });
      map.addLayer({ id: 'preview-footprint-fill', type: 'fill', source: 'preview-footprint', paint: { 'fill-color': '#2684ff', 'fill-opacity': 0.28 } });
      map.addLayer({ id: 'preview-footprint-line', type: 'line', source: 'preview-footprint', paint: { 'line-color': '#1677ff', 'line-width': 3 } });
      const coordinates = previewFootprint.type === 'MultiPolygon' ? previewFootprint.coordinates.flat(2) : previewFootprint.coordinates.flat(1);
      if (coordinates.length) {
        const bounds = coordinates.reduce((result, point) => result.extend(point), new maplibregl.LngLatBounds(coordinates[0], coordinates[0]));
        map.fitBounds(bounds, { padding: 36, maxZoom: 19, duration: 0 });
      }
    });
    const frame = requestAnimationFrame(() => map.resize());
    return () => { cancelAnimationFrame(frame); marker.remove(); map.remove(); };
  }, [footprintKey, locationKey]);
  return <Box ref={nodeRef} sx={{ height: 190, borderRadius: 2, overflow: 'hidden', bgcolor: 'action.hover' }} />;
}
function MobileLocationEditor({ open, initialLocation, fallbackCenter, onClose, onSave, title, saveLabel, cancelLabel, useMyLocationLabel, locationErrorText, fullScreen = true }) {
  const mapNode = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [draft, setDraft] = useState(initialLocation);
  const [dialogEntered, setDialogEntered] = useState(false);
  const [locationError, setLocationError] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(initialLocation);
      setLocationError(false);
    }
  }, [initialLocation, open]);

  useEffect(() => {
    if (!open || !dialogEntered || !mapNode.current) return undefined;
    const center = initialLocation || fallbackCenter;
    const map = new maplibregl.Map({ container: mapNode.current, style: locationEditorMapStyle, center, zoom: 18, pitch: 25 });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
    const markerElement = document.createElement('div');
    markerElement.className = 'place-marker';
    markerElement.style.setProperty('--marker-color', '#1677ff');
    const markerPin = document.createElement('span');
    markerPin.className = 'marker-pin';
    markerElement.append(markerPin);
    const marker = new maplibregl.Marker({ element: markerElement, anchor: 'bottom', draggable: true })
      .setLngLat(center).addTo(map);
    marker.on('dragend', () => {
      const position = marker.getLngLat();
      setDraft([Number(position.lng.toFixed(7)), Number(position.lat.toFixed(7))]);
    });
    map.on('click', (event) => {
      const next = [Number(event.lngLat.lng.toFixed(7)), Number(event.lngLat.lat.toFixed(7))];
      marker.setLngLat(next);
      setDraft(next);
    });
    markerRef.current = marker;
    mapRef.current = map;
    const frame = requestAnimationFrame(() => map.resize());
    return () => {
      cancelAnimationFrame(frame);
      marker.remove();
      map.remove();
      markerRef.current = null;
      mapRef.current = null;
    };
  }, [dialogEntered, fallbackCenter, initialLocation, open]);

  const moveToCurrentLocation = () => {
    setLocationError(false);
    if (!navigator.geolocation) {
      setLocationError(true);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const next = [Number(coords.longitude.toFixed(7)), Number(coords.latitude.toFixed(7))];
        setDraft(next);
        markerRef.current?.setLngLat(next);
        mapRef.current?.flyTo({ center: next, zoom: 18, duration: 700 });
      },
      () => setLocationError(true),
      { enableHighAccuracy: true, timeout: 12000 },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth={"md"}
      slotProps={{ transition: { onEntered: () => setDialogEntered(true), onExited: () => setDialogEntered(false) } }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5 }}>
        {locationError && <Alert severity="warning">{locationErrorText}</Alert>}
        <Box sx={{ position: 'relative', flex: 1, minHeight: 360 }}>
          <Box ref={mapNode} sx={{ position: 'absolute', inset: 0, borderRadius: 2, overflow: 'hidden', bgcolor: 'action.hover' }} />
          <IconButton
            onClick={moveToCurrentLocation}
            aria-label={useMyLocationLabel}
            sx={{ position: 'absolute', right: 12, top: 12, zIndex: 2, bgcolor: 'background.paper', boxShadow: 3, '&:hover': { bgcolor: 'background.paper' } }}
          >
            <MyLocationRoundedIcon color="primary" />
          </IconButton>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{cancelLabel}</Button>
        <Button variant="contained" disabled={!draft} onClick={() => { onSave(draft); onClose(); }}>{saveLabel}</Button>
      </DialogActions>
    </Dialog>
  );
}
export default function PlaceEditDialog({ open, place, onClose, mode = 'edit' }) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 599.95px), (max-height: 499.95px)');
  const isNarrowMobile = useMediaQuery('(max-width: 899.95px)');
  const isCreate = mode === 'create';
  const isMobileCreate = isCreate && isNarrowMobile;
  const isDesktopCreate = isCreate && !isMobileCreate;
  const isMobileEdit = !isCreate && isNarrowMobile;
  const mapNode = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const businessMarkerRef = useRef(null);
  const pointsRef = useRef([]);
  const buildingLinesRef = useRef([]);
  const selectBuildingModeRef = useRef(false);
  const selectingLocationRef = useRef(false);
  const [points, setPoints] = useState([]);
  const [businessLocation, setBusinessLocation] = useState(null);
  const [selectingLocation, setSelectingLocation] = useState(false);
  const [mobileLocationEditorOpen, setMobileLocationEditorOpen] = useState(false);
  const [polygonEditorOpen, setPolygonEditorOpen] = useState(false);
  const [polygonDialogEntered, setPolygonDialogEntered] = useState(false);
  const [continuation, setContinuation] = useState(null);
  const [continuationLoading, setContinuationLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [createStep, setCreateStep] = useState(0);
  const [rememberContact, setRememberContact] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState('');
  const [copied, setCopied] = useState(false);
  const [dialogEntered, setDialogEntered] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [buildingLines, setBuildingLines] = useState([]);
  const [buildingsLoading, setBuildingsLoading] = useState(false);
  const [buildingsUnavailable, setBuildingsUnavailable] = useState(false);
  const [magnetEnabled, setMagnetEnabled] = useState(true);
  const [selectBuildingMode, setSelectBuildingMode] = useState(false);
  const originalPoints = useMemo(() => pointsFromPlace(place), [place]);
  const polygon = useMemo(() => {
    if (points.length < 3) return null;
    const editedRing = [...points, points[0]];
    if (place?.footprint?.type === 'MultiPolygon') {
      return {
        type: 'MultiPolygon',
        coordinates: [[editedRing], ...(place.footprint.coordinates || []).slice(1)],
      };
    }
    return { type: 'Polygon', coordinates: [editedRing] };
  }, [place, points]);
  useEffect(() => {
    if (!open || !isCreate) return undefined;
    let active = true;
    setCategoriesLoading(true);
    getPlaceCategories()
      .then((items) => { if (active) setAvailableCategories(Array.isArray(items) ? items : []); })
      .catch(() => { if (active) setAvailableCategories([]); })
      .finally(() => { if (active) setCategoriesLoading(false); });
    return () => { active = false; };
  }, [isCreate, open]);


  useEffect(() => {
    if (!open || !place) return;
    const savedContact = loadSavedContact();
    setRememberContact(Boolean(savedContact));
    setCreateStep(0);
    setPoints(Array.isArray(place.continuationPoints) ? place.continuationPoints.map((point) => [...point]) : pointsFromPlace(place));
    const longitude = Number(place?.longitude);
    const latitude = Number(place?.latitude);
    setBusinessLocation((!isCreate || isMobileCreate || place.isContinuationDraft) && Number.isFinite(longitude) && Number.isFinite(latitude) ? [longitude, latitude] : null);
    setSelectingLocation(isCreate && !isMobileCreate);
    setMobileLocationEditorOpen(false);
    setPolygonEditorOpen(false);
    setContinuation(null);
    setResult('');
    setCopied(false);
    setMapError(false);
    setSelectBuildingMode(false);
    setForm(place.continuationForm
      ? {
          ...place.continuationForm,
          description: place.continuationForm.description || '',
          firstName: place.continuationForm.firstName ?? savedContact?.firstName ?? '',
          lastName: place.continuationForm.lastName ?? savedContact?.lastName ?? '',
          phone: place.continuationForm.phone ?? savedContact?.phone ?? '',
          email: place.continuationForm.email ?? savedContact?.email ?? '',
          categories: Array.isArray(place.continuationForm.categories) ? place.continuationForm.categories : [],
          openingHours: (place.continuationForm.openingHours || []).map((day) => ({
            ...day,
            ranges: (day.ranges || []).map((range) => ({ ...range })),
          })),
        }
      : {
          name: place.name || place.displayName || '',
          description: typeof place.description === 'string' ? place.description : '',
          address: place.address || '',
          city: place.city || '',
          country: place.country || '',
          categories: Array.isArray(place.categories) ? place.categories : [],
          openingHours: toHours(place.openingHours),
          firstName: place.reporter?.firstName || savedContact?.firstName || '',
          lastName: place.reporter?.lastName || savedContact?.lastName || '',
          phone: place.reporter?.phone || savedContact?.phone || '',
          email: place.reporter?.email || savedContact?.email || '',
        });
  }, [isCreate, isMobileCreate, open, place]);

  useEffect(() => {
    if (!rememberContact || !form) return;
    saveContact({
      firstName: form.firstName || '',
      lastName: form.lastName || '',
      phone: form.phone || '',
      email: form.email || '',
    });
  }, [form, rememberContact]);

  useEffect(() => {
    buildingLinesRef.current = buildingLines;
  }, [buildingLines]);
  useEffect(() => {
    pointsRef.current = points;
  }, [points]);
  useEffect(() => {
    selectBuildingModeRef.current = selectBuildingMode;
  }, [selectBuildingMode]);
  useEffect(() => {
    selectingLocationRef.current = selectingLocation;
  }, [selectingLocation]);

  useEffect(() => {
    if (!open || !place) return undefined;
    const footprintPoint = pointsFromPlace(place)[0];
    const latitude = isCreate ? businessLocation?.[1] : (Number.isFinite(Number(place?.latitude)) ? Number(place?.latitude) : footprintPoint?.[1]);
    const longitude = isCreate ? businessLocation?.[0] : (Number.isFinite(Number(place?.longitude)) ? Number(place?.longitude) : footprintPoint?.[0]);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return undefined;
    const controller = new AbortController();
    setBuildingsLoading(true);
    setBuildingsUnavailable(false);
    loadNearbyBuildingLines(latitude, longitude, controller.signal)
      .then((lines) => {
        setBuildingLines(lines);
        setBuildingsUnavailable(lines.length === 0);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          setBuildingLines([]);
          setBuildingsUnavailable(true);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setBuildingsLoading(false);
      });
    return () => controller.abort();
  }, [businessLocation, isCreate, isMobileCreate, open, place]);

  const geometryEditorReady = isDesktopCreate ? polygonDialogEntered : dialogEntered;

  useEffect(() => {
    if (!open || !geometryEditorReady || !mapNode.current || !place || mapRef.current || isMobileEdit) {
      return undefined;
    }
    const footprintPoint = pointsFromPlace(place)[0];
    const longitude = Number(place?.longitude);
    const latitude = Number(place?.latitude);
    const center = businessLocation
      ? businessLocation
      : Number.isFinite(longitude) && Number.isFinite(latitude)
        ? [longitude, latitude]
        : footprintPoint || [34.9519, 29.5577];
    let map;
    try {
      map = new maplibregl.Map({
        container: mapNode.current,
        style: locationEditorMapStyle,
        center,
        zoom: 18,
        pitch: 35,
      });
    } catch {
      setMapError(true);
      return undefined;
    }
    if (!isMobileCreate) map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
    if (isMobileCreate) {
      map.dragPan.disable();
      map.scrollZoom.disable();
      map.boxZoom.disable();
      map.dragRotate.disable();
      map.keyboard.disable();
      map.doubleClickZoom.disable();
      map.touchZoomRotate.disable();
    }
    if (!isCreate && businessLocation) {
      const markerElement = document.createElement('div');
      const markerPin = document.createElement('span');
      const markerName = document.createElement('span');
      markerElement.className = 'place-marker';
      markerElement.style.setProperty('--marker-color', '#1677ff');
      markerPin.className = 'marker-pin';
      markerName.className = 'marker-name';
      markerName.style.display = 'block';
      markerName.textContent = place.name || place.displayName || '';
      markerElement.append(markerPin, markerName);
      const businessMarker = new maplibregl.Marker({
        element: markerElement,
        anchor: 'bottom',
        draggable: true,
      })
        .setLngLat(businessLocation)
        .addTo(map);
      businessMarker.on('dragend', () => {
        const position = businessMarker.getLngLat();
        setBusinessLocation([
          Number(position.lng.toFixed(7)),
          Number(position.lat.toFixed(7)),
        ]);
      });
      businessMarkerRef.current = businessMarker;
    }
    map.on('load', () => {
      map.addSource('nearby-buildings', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'nearby-buildings-fill', type: 'fill', source: 'nearby-buildings',
        filter: ['==', ['get', 'closed'], true],
        paint: {
          'fill-color': '#12b76a',
          'fill-opacity': ['case', ['boolean', ['get', 'selectable'], false], 0.16, 0.04],
        },
      });
      map.addLayer({
        id: 'nearby-buildings-line', type: 'line', source: 'nearby-buildings',
        paint: {
          'line-color': ['case', ['boolean', ['get', 'selectable'], false], '#087443', '#1570ef'],
          'line-width': ['case', ['boolean', ['get', 'selectable'], false], 3, 1.5],
          'line-opacity': 0.65,
        },
      });
      setBuildingLines((current) => [...current]);
      map.addSource('proposal', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'proposal-fill', type: 'fill', source: 'proposal',
        paint: { 'fill-color': '#2684ff', 'fill-opacity': 0.28 },
      });
      map.addLayer({
        id: 'proposal-line', type: 'line', source: 'proposal',
        paint: { 'line-color': '#1677ff', 'line-width': 3 },
      });
      setPoints((current) => [...current]);
      map.resize();
    });
    map.on('click', (event) => {
      if (isMobileCreate) return;
      if (isCreate && selectingLocationRef.current) {
        setBusinessLocation([Number(event.lngLat.lng.toFixed(7)), Number(event.lngLat.lat.toFixed(7))]);
        setSelectingLocation(false);
        return;
      }
      if (selectBuildingModeRef.current) {
        const feature = map.queryRenderedFeatures(event.point, {
          layers: ['nearby-buildings-fill'],
        })[0];
        const building = buildingLinesRef.current.find((line) => line.id === feature?.properties?.buildingId);
        if (building?.isClosed) {
          if (pointsRef.current.length && !window.confirm(t('placeEdit.replacePolygonConfirm'))) return;
          setPoints(building.points.slice(0, -1).map(([lng, lat]) => [
            Number(lng.toFixed(7)), Number(lat.toFixed(7)),
          ]));
          setSelectBuildingMode(false);
        }
        return;
      }
      const point = [
        Number(event.lngLat.lng.toFixed(7)),
        Number(event.lngLat.lat.toFixed(7)),
      ];
      setPoints((current) => addPolygonPoint(current, point, map));
    });
    mapRef.current = map;
    const resizeFrame = requestAnimationFrame(() => map.resize());
    const resizeTimer = window.setTimeout(() => map.resize(), 250);
    return () => {
      cancelAnimationFrame(resizeFrame);
      window.clearTimeout(resizeTimer);
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      businessMarkerRef.current?.remove();
      businessMarkerRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, [businessLocation, createStep, geometryEditorReady, isCreate, isMobileCreate, isMobileEdit, open, place, t]);

  useEffect(() => {
    const map = mapRef.current;
    const source = map?.getSource('nearby-buildings');
    if (!source) return;
    source.setData({
      type: 'FeatureCollection',
      features: buildingLines.map((line) => ({
        type: 'Feature',
        properties: {
          buildingId: line.id,
          closed: line.isClosed,
          selectable: selectBuildingMode && line.isClosed,
        },
        geometry: line.isClosed
          ? { type: 'Polygon', coordinates: [line.points] }
          : { type: 'LineString', coordinates: line.points },
      })),
    });
    map.getCanvas().style.cursor = selectBuildingMode ? 'crosshair' : '';
  }, [buildingLines, selectBuildingMode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !businessLocation || !isCreate) return;
    businessMarkerRef.current?.remove();
    const markerElement = document.createElement('div');
    markerElement.className = 'place-marker';
    markerElement.style.setProperty('--marker-color', '#1677ff');
    const markerPin = document.createElement('span');
    markerPin.className = 'marker-pin';
    markerElement.append(markerPin);
    const marker = new maplibregl.Marker({ element: markerElement, anchor: 'bottom', draggable: !isMobileCreate }).setLngLat(businessLocation).addTo(map);
    marker.on('dragend', () => {
      const position = marker.getLngLat();
      setBusinessLocation([Number(position.lng.toFixed(7)), Number(position.lat.toFixed(7))]);
    });
    businessMarkerRef.current = marker;
  }, [businessLocation, isCreate, isMobileCreate]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = points.map((point, index) => {
      const marker = new maplibregl.Marker({ draggable: true, color: '#1677ff' })
        .setLngLat(point).addTo(map);
      marker.on('dragend', () => {
        const position = marker.getLngLat();
        const draggedPoint = [position.lng, position.lat];
        const snap = magnetEnabled
          ? findBuildingSnapPoint(draggedPoint, buildingLines, 0.5)
          : null;
        const nextPoint = snap?.point || draggedPoint;
        setPoints((current) => current.map((item, itemIndex) =>
          itemIndex === index
            ? [Number(nextPoint[0].toFixed(7)), Number(nextPoint[1].toFixed(7))]
            : item));
      });
      return marker;
    });
    const source = map.getSource('proposal');
    source?.setData({
      type: 'FeatureCollection',
      features: polygon ? [{ type: 'Feature', properties: {}, geometry: polygon }] : [],
    });
  }, [buildingLines, magnetEnabled, points, polygon]);

  useEffect(() => {
    if (!continuation?.token || continuation.opened) return undefined;
    const timer = window.setInterval(() => {
      getPlaceContinuationDraftStatus(continuation.token)
        .then((status) => {
          if (status.status === 'OPENED') setContinuation((current) => current ? { ...current, opened: true } : current);
        })
        .catch(() => { });
    }, 2000);
    return () => window.clearInterval(timer);
  }, [continuation?.opened, continuation?.token]);
  if (!form) return null;
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const canAdvanceCreateStep = (() => {
    if (!isCreate) return true;
    if (createStep === 0) return Boolean(form.name && form.city && form.country);
    if (createStep === 1) return !categoriesLoading && form.categories.length > 0;
    if (createStep === 2) return Boolean(
      businessLocation && (isMobileCreate || (polygon && pointInPolygon(businessLocation, points)))
    );
    return true;
  })();
  const submit = async () => {
    setSending(true);
    setResult('');
    try {
      const payload = {
        reporter: {
          firstName: form.firstName, lastName: form.lastName,
          phone: form.phone, email: form.email,
        },
        proposed: {
          name: form.name, description: form.description || '', address: form.address, city: form.city, country: form.country,
          ...(isCreate ? { categories: form.categories } : {}),
          openingHours: serializeHours(form.openingHours), footprint: isMobileCreate ? null : polygon,
          location: businessLocation ? { type: 'Point', coordinates: businessLocation } : null,
        },
      };
      const data = isCreate
        ? await submitNewPlace(payload)
        : await submitPlaceEdit({ placeId: place.id, ...payload });
      setResult(isCreate ? data.placeId : data.submissionId);
    } catch (error) {
      setResult(`error:${error.message}`);
    } finally {
      setSending(false);
    }
  };
  const continueOnDesktop = async () => {
    setContinuationLoading(true);
    try {
      const data = await createPlaceContinuationDraft({
        source: 'web-mobile',
        payload: {
          kind: isCreate ? 'place-create' : 'place-edit-geometry',
          ...(isCreate ? {} : { placeId: place.id }),
          snapshot: {
            place: {
              ...place,
              name: form.name,
              address: form.address,
              city: form.city,
              country: form.country,
              openingHours: serializeHours(form.openingHours),
              footprint: isCreate ? null : polygon,
              latitude: businessLocation?.[1] ?? place.latitude,
              longitude: businessLocation?.[0] ?? place.longitude,
              reporter: {
                firstName: form.firstName,
                lastName: form.lastName,
                phone: form.phone,
                email: form.email,
              },
            },
            form: {
              ...form,
              openingHours: form.openingHours.map((day) => ({
                ...day,
                ranges: day.ranges.map((range) => ({ ...range })),
              })),
            },
            points: points.map((point) => [...point]),
            businessLocation: businessLocation ? [...businessLocation] : null,
          },
          reporter: { firstName: form.firstName, lastName: form.lastName, phone: form.phone, email: form.email },
          proposed: {
            name: form.name, description: form.description || '', address: form.address, city: form.city, country: form.country,
            categories: form.categories,
            openingHours: serializeHours(form.openingHours), footprint: isCreate ? null : polygon,
            location: businessLocation ? { type: 'Point', coordinates: businessLocation } : null,
          },
        },
      });
      setContinuation({ ...data, opened: false });
    } catch (error) {
      setResult(`error:${error.message}`);
    } finally {
      setContinuationLoading(false);
    }
  };

  const copySubmissionId = async () => {
    try {
      await navigator.clipboard.writeText(result);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };


  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth={result ? "xs" : "md"}
      slotProps={{
        transition: {
          onEntered: () => setDialogEntered(true),
          onExit: () => setDialogEntered(false),
        },
      }}
    >
      <DialogTitle>
        {t(result ? (isCreate ? 'addPlace.successTitle' : 'placeEdit.success_1') : (isCreate ? 'addPlace.title' : 'placeEdit.title'))}
        <IconButton onClick={onClose} sx={{ position: 'absolute', insetInlineEnd: 12, top: 10 }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={isCreate && !result ? { flex: 1, minHeight: 0, overflowY: 'auto' } : undefined}>
        {result && !result.startsWith('error:')
          ? <Stack spacing={1} sx={{ alignItems: "center" }}>

            <Typography sx={{ fontWeight: "bold" }}>
              {t(isCreate ? 'addPlace.successText' : 'placeEdit.success_2')}
            </Typography>

            <Stack sx={{
              padding: 1,
              paddingX: 2,
              bgcolor: "#bffc8e",
              borderRadius: 1,
              alignItems: "center",
              justifyContent: "center"
            }}>
              <Stack sx={{ flexDirection: "row" }}
              >
                <Typography variant='h6'>{result}</Typography>
                <IconButton
                  size="small"
                  onClick={copySubmissionId}
                  aria-label={t(copied ? 'placeEdit.copied' : 'placeEdit.copyId')}
                  title={t(copied ? 'placeEdit.copied' : 'placeEdit.copyId')}
                >
                  <ContentCopyRoundedIcon fontSize="small" />
                </IconButton>
              </Stack>

              <Typography variant='body2'>
                {t(isCreate ? 'addPlace.successHint' : 'placeEdit.success_3')}
              </Typography>
            </Stack>

          </Stack>

          : <Stack spacing={2}>
            {isCreate && (
              <Stack direction="row" sx={{ width: '100%', pb: 0.5 }}>
                {CREATE_STEPS.map((key, index) => (
                  <Stack key={key} sx={{ flex: 1, minWidth: 0, alignItems: 'center', position: 'relative' }}>
                    <Box sx={{ width: { xs: 26, sm: 30 }, height: { xs: 26, sm: 30 }, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: index <= createStep ? 'primary.main' : 'action.disabledBackground', color: '#fff', fontWeight: 800, fontSize: { xs: 12, sm: 14 } }}>
                      {index + 1}
                    </Box>
                    <Typography sx={{ mt: 0.5, px: 0.25, fontSize: { xs: 9, sm: 11 }, lineHeight: 1.15, fontWeight: 700, color: index <= createStep ? 'primary.main' : 'text.secondary', textAlign: 'center', overflowWrap: 'anywhere' }}>
                      {t(`addPlace.steps.${key}`)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
            {result.startsWith('error:') && <Alert severity="error">{t('placeEdit.failed')}</Alert>}

            {(!isCreate || createStep === 0) && <Stack>
              <Typography fontWeight={800}>{t('placeEdit.businessDetails')}</Typography>
              <Stack spacing={1}>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField fullWidth required label={t('placeEdit.name')} value={form.name} onChange={(e) => update('name', e.target.value)} />
                  <TextField fullWidth label={t('placeEdit.address')} value={form.address} onChange={(e) => update('address', e.target.value)} />
                </Stack>

                <Stack direction='row' spacing={1.5}>
                  <TextField fullWidth label={t('placeEdit.city')} value={form.city} onChange={(e) => update('city', e.target.value)} />
                  <TextField fullWidth label={t('placeEdit.country')} value={form.country} onChange={(e) => update('country', e.target.value)} />
                </Stack>
                {isCreate && <TextField fullWidth multiline minRows={3} label={t('addPlace.descriptionLabel')} value={form.description || ''} helperText={t('addPlace.descriptionHelp', { count: countWords(form.description) })} onChange={(event) => { if (countWords(event.target.value) <= 150) update('description', event.target.value); }} />}
              </Stack>
            </Stack>}

            {isCreate && createStep === 1 && (
              <Stack spacing={1} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Typography fontWeight={800}>{t('addPlace.categoriesTitle')}</Typography>
                <Typography color="text.secondary" fontSize={13}>{t('addPlace.categoriesHelp')}</Typography>
                {categoriesLoading
                  ? <Typography color="text.secondary">{t('addPlace.categoriesLoading')}</Typography>
                  : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 0.5 }}>
                      {availableCategories.map((category) => (
                        <FormControlLabel
                          key={category.systemName}
                          control={(
                            <Checkbox
                              checked={form.categories.includes(category.systemName)}
                              onChange={(event) => update('categories', event.target.checked
                                ? [...new Set([...form.categories, category.systemName])]
                                : form.categories.filter((item) => item !== category.systemName))}
                            />
                          )}
                          label={category.displayName || category.systemName.replaceAll('_', ' ')}
                        />
                      ))}
                    </Box>
                  )}
                {!form.categories.length && <Alert severity="info">{t('addPlace.categoryRequired')}</Alert>}
              </Stack>
            )}
            {isDesktopCreate && createStep === 2 && (
              <Stack spacing={2}>
                <Stack spacing={1.25} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Typography fontWeight={800}>{t('addPlace.locationTitle')}</Typography>
                  {!businessLocation && <Alert severity="info">{t('addPlace.locationRequired')}</Alert>}
                  <GeometryPreview location={businessLocation || [Number(place?.longitude) || 34.9519, Number(place?.latitude) || 29.5577]} footprint={null} />
                  <Button variant="outlined" onClick={() => setMobileLocationEditorOpen(true)}>{t('addPlace.editLocation')}</Button>
                </Stack>

                <Stack spacing={1.25} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Typography fontWeight={800}>{t('placeEdit.polygon')}</Typography>
                  <Typography color="text.secondary" fontSize={13}>{t('placeEdit.polygonHelp')}</Typography>
                  <GeometryPreview location={businessLocation || [Number(place?.longitude) || 34.9519, Number(place?.latitude) || 29.5577]} footprint={polygon} />
                  <Button variant="outlined" disabled={!businessLocation} onClick={() => setPolygonEditorOpen(true)}>{t('addPlace.editPolygon')}</Button>
                </Stack>
              </Stack>
            )}

            {isMobileEdit ? (
              <Stack spacing={1.25} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Typography fontWeight={800}>{t('placeEdit.geometryTitle')}</Typography>
                <GeometryPreview location={businessLocation} footprint={polygon} />
                <Alert severity="info">
                  <Stack spacing={1}>
                    <Typography>{t('addPlace.polygonDesktopOnly')}</Typography>
                    <Button variant="contained" disabled={continuationLoading} onClick={continueOnDesktop}>
                      {t('addPlace.continueDesktop')}
                    </Button>
                  </Stack>
                </Alert>
              </Stack>
            ) : (!isCreate || createStep === 2) && !isDesktopCreate && <>
              {isCreate &&
                <Stack>
                  <Typography fontWeight={800}>{t('addPlace.locationTitle')}</Typography>
                  {!isMobileCreate && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button variant={selectingLocation ? 'contained' : 'outlined'} onClick={() => setSelectingLocation(true)}>{t('addPlace.pickLocation')}</Button>
                    <Button onClick={() => navigator.geolocation?.getCurrentPosition(({ coords }) => { setBusinessLocation([coords?.longitude, coords?.latitude]); setSelectingLocation(false); mapRef.current?.flyTo({ center: [coords?.longitude, coords?.latitude], zoom: 18 }); })}>{t('addPlace.useMyLocation')}</Button>
                  </Stack>}
                  {!businessLocation && <Alert severity="info">{t('addPlace.locationRequired')}</Alert>}
                </Stack>
              }

              {!isMobileCreate &&
                <Stack>
                  <Typography fontWeight={800}>{t('placeEdit.polygon')}</Typography>
                  <Typography color="text.secondary" fontSize={13}>{t('placeEdit.polygonHelp')}</Typography>
                </Stack>
              }

              {mapError && <Alert severity="warning">{t('placeEdit.mapFailed')}</Alert>}

              <Box
                ref={mapNode}
                sx={{
                  position: 'relative',
                  display: 'block',
                  width: '100%',
                  height: { xs: '44dvh', sm: 330 },
                  minHeight: { xs: 250, sm: 330 },
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'action.hover',
                }}
              />

              {isMobileCreate &&
                <Stack spacing={2}>
                  <Button variant="outlined" onClick={() => setMobileLocationEditorOpen(true)}>
                    {t('addPlace.editLocation')}
                  </Button>

                  <Alert severity="info">
                    <Stack spacing={1}>
                      <Typography>{t('addPlace.polygonDesktopOnly')}</Typography>
                      <Button variant="contained" disabled={continuationLoading} onClick={continueOnDesktop}>{t('addPlace.continueDesktop')}</Button>
                    </Stack>
                  </Alert>
                </Stack>
              }

              {!isMobileCreate &&
                <Stack>
                  <Stack direction="row" spacing={1}>
                    <Button onClick={() => setPoints((current) => current.slice(0, -1))}>{t('placeEdit.undoPoint')}</Button>
                    <Button color="error" onClick={() => setPoints([])}>{t('placeEdit.clearPolygon')}</Button>
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                    {originalPoints.length >= 3 && (
                      <Button variant="outlined" onClick={() => setPoints(originalPoints.map((point) => [...point]))}>
                        {t('placeEdit.restoreOriginal')}
                      </Button>
                    )}
                    <FormControlLabel
                      control={<Checkbox checked={magnetEnabled} onChange={(event) => setMagnetEnabled(event.target.checked)} />}
                      label={t('placeEdit.buildingMagnet')}
                    />
                    <Button
                      variant={selectBuildingMode ? 'contained' : 'outlined'}
                      disabled={buildingsLoading || !buildingLines.some((line) => line.isClosed)}
                      onClick={() => setSelectBuildingMode((current) => !current)}
                    >
                      {selectBuildingMode ? t('placeEdit.cancelBuilding') : t('placeEdit.selectBuilding')}
                    </Button>
                  </Stack>
                  {buildingsLoading && <Typography color="text.secondary" fontSize={13}>{t('placeEdit.loadingBuildings')}</Typography>}
                  {!buildingsLoading && buildingsUnavailable && (
                    <Typography color="text.secondary" fontSize={13}>{t('placeEdit.noBuildings')}</Typography>
                  )}
                  {selectBuildingMode && (
                    <Alert severity="info">{t('placeEdit.selectBuildingHelp')}</Alert>
                  )}
                </Stack>
              }
            </>}

            {(!isCreate || createStep === 3) && <Stack>
              <Typography fontWeight={800}>{t('placeEdit.openingHours')}</Typography>

              <Stack spacing={1}>
                {form.openingHours.map((day, index) => (
                  <Stack key={day.day} spacing={1} sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }} >
                      <Typography fontWeight={800}>{t(`openingHours.days.${day.day}`)}</Typography>
                      <Button
                        size="small"
                        disabled={day.ranges.length >= 5}
                        onClick={() => {
                          const next = [...form.openingHours];
                          next[index] = {
                            ...day,
                            ranges: [...day.ranges, { open: 540, close: 1020, isOpen24Hours: false }],
                          };
                          update('openingHours', next);
                        }}
                      >
                        {t('placeEdit.addRange')}
                      </Button>
                    </Stack>

                    {!day.ranges.length &&
                      <Typography
                        sx={{
                          textAlign: "center",
                          fontSize: 13,
                          fontWeight: "bold"
                        }}
                        color="text.secondary"
                      >
                        {t('placeEdit.closed')}
                      </Typography>
                    }

                    {day.ranges.map((range, rangeIndex) => (
                      <Stack
                        key={`${day.day}-${rangeIndex}`}
                        direction={{ xs: 'column', sm: 'row' }}
                        sx={{ alignItems: { sm: 'center' } }}
                        spacing={1}
                      >

                        <FormControlLabel
                          control={<Checkbox checked={range.isOpen24Hours} onChange={(event) => {
                            const next = [...form.openingHours];
                            const ranges = [...day.ranges];
                            ranges[rangeIndex] = { ...range, isOpen24Hours: event.target.checked };
                            next[index] = { ...day, ranges };
                            update('openingHours', next);
                          }} />}
                          label={t('placeEdit.open24')}
                        />
                        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>

                          {!range.isOpen24Hours && <>
                            <TextField type="time" size="small" value={toTime(range.open)} onChange={(event) => {
                              const next = [...form.openingHours];
                              const ranges = [...day.ranges];
                              ranges[rangeIndex] = { ...range, open: fromTime(event.target.value) };
                              next[index] = { ...day, ranges };
                              update('openingHours', next);
                            }} />
                            <Typography>{t('placeEdit.until')}</Typography>
                            <TextField type="time" size="small" value={toTime(range.close)} onChange={(event) => {
                              const next = [...form.openingHours];
                              const ranges = [...day.ranges];
                              ranges[rangeIndex] = { ...range, close: fromTime(event.target.value) };
                              next[index] = { ...day, ranges };
                              update('openingHours', next);
                            }} />
                          </>}

                        </Stack>

                        <Button color="error" size="small" onClick={() => {
                          const next = [...form.openingHours];
                          next[index] = {
                            ...day,
                            ranges: day.ranges.filter((_, itemIndex) => itemIndex !== rangeIndex),
                          };
                          update('openingHours', next);
                        }}>
                          {t('placeEdit.deleteRange')}
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                ))}
              </Stack>
            </Stack>}

            {(!isCreate || createStep === 4) && <Stack>
              <Typography fontWeight={800}>{t('placeEdit.contact')}</Typography>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={rememberContact}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setRememberContact(checked);
                      if (!checked) clearSavedContact();
                      else saveContact({
                        firstName: form.firstName || '',
                        lastName: form.lastName || '',
                        phone: form.phone || '',
                        email: form.email || '',
                      });
                    }}
                  />
                )}
                label={t('placeEdit.rememberContact')}
              />

              <Stack spacing={1}>
                <Stack direction='row' spacing={1.5}>
                  <TextField required fullWidth label={t('placeEdit.firstName')} value={form.firstName} onChange={(e) => update('firstName', e.target.value)} />
                  <TextField required fullWidth label={t('placeEdit.lastName')} value={form.lastName} onChange={(e) => update('lastName', e.target.value)} />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField required fullWidth label={t('placeEdit.phone')} value={form.phone} onChange={(e) => update('phone', e.target.value)} />
                  <TextField required type="email" fullWidth label={t('placeEdit.email')} value={form.email} onChange={(e) => update('email', e.target.value)} />
                </Stack>
              </Stack>
            </Stack>}

          </Stack>
        }

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('placeEdit.close')}</Button>
        {!result || result.startsWith('error:') ? (
          isCreate ? <>
            {createStep > 0 && <Button onClick={() => setCreateStep((current) => current - 1)}>{t('addPlace.back')}</Button>}
            {createStep < CREATE_STEPS.length - 1 ? (
              <Button variant="contained" disabled={!canAdvanceCreateStep} onClick={() => setCreateStep((current) => current + 1)}>
                {t('addPlace.next')}
              </Button>
            ) : (
              <Button variant="contained" disabled={sending || !form.firstName || !form.lastName || !form.phone || !form.email} onClick={submit}>
                {sending ? t('placeEdit.sending') : t('addPlace.submit')}
              </Button>
            )}
          </> : (
            <Button variant="contained" disabled={sending || !form.name || !form.city || !form.country || !form.firstName || !form.lastName || !form.phone || !form.email || !businessLocation || (!isMobileCreate && !polygon)} onClick={submit}>
              {sending ? t('placeEdit.sending') : t('placeEdit.submit')}
            </Button>
          )
        ) : null}
      </DialogActions>
      {isDesktopCreate && createStep === 2 && (
        <Dialog
          open={polygonEditorOpen}
          onClose={() => setPolygonEditorOpen(false)}
          fullWidth
          maxWidth="md"
          slotProps={{ transition: { onEntered: () => setPolygonDialogEntered(true), onExited: () => setPolygonDialogEntered(false) } }}
        >
          <DialogTitle>{t('addPlace.polygonEditorTitle')}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.5}>
              <Typography color="text.secondary" fontSize={13}>{t('placeEdit.polygonHelp')}</Typography>
              {mapError && <Alert severity="warning">{t('placeEdit.mapFailed')}</Alert>}
              <Box ref={mapNode} sx={{ position: 'relative', width: '100%', height: 480, minHeight: 360, borderRadius: 2, overflow: 'hidden', bgcolor: 'action.hover' }} />
              <Stack direction="row" spacing={1}>
                <Button onClick={() => setPoints((current) => current.slice(0, -1))}>{t('placeEdit.undoPoint')}</Button>
                <Button color="error" onClick={() => setPoints([])}>{t('placeEdit.clearPolygon')}</Button>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                {originalPoints.length >= 3 && (
                  <Button variant="outlined" onClick={() => setPoints(originalPoints.map((point) => [...point]))}>{t('placeEdit.restoreOriginal')}</Button>
                )}
                <FormControlLabel control={<Checkbox checked={magnetEnabled} onChange={(event) => setMagnetEnabled(event.target.checked)} />} label={t('placeEdit.buildingMagnet')} />
                <Button
                  variant={selectBuildingMode ? 'contained' : 'outlined'}
                  disabled={buildingsLoading || !buildingLines.some((line) => line.isClosed)}
                  onClick={() => setSelectBuildingMode((current) => !current)}
                >
                  {selectBuildingMode ? t('placeEdit.cancelBuilding') : t('placeEdit.selectBuilding')}
                </Button>
              </Stack>
              {buildingsLoading && <Typography color="text.secondary" fontSize={13}>{t('placeEdit.loadingBuildings')}</Typography>}
              {!buildingsLoading && buildingsUnavailable && <Typography color="text.secondary" fontSize={13}>{t('placeEdit.noBuildings')}</Typography>}
              {selectBuildingMode && <Alert severity="info">{t('placeEdit.selectBuildingHelp')}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={() => setPolygonEditorOpen(false)}>{t('addPlace.savePolygon')}</Button>
          </DialogActions>
        </Dialog>
      )}

      {isCreate && (
        <MobileLocationEditor
          open={mobileLocationEditorOpen}
          initialLocation={businessLocation}
          fallbackCenter={[Number(place?.longitude) || 34.9519, Number(place?.latitude) || 29.5577]}
          title={t('addPlace.locationEditorTitle')}
          saveLabel={t('addPlace.saveLocation')}
          cancelLabel={t('placeEdit.close')}
          useMyLocationLabel={t('addPlace.useMyLocation')}
          locationErrorText={t('addPlace.locationError')}
          fullScreen={isMobileCreate}
          onClose={() => setMobileLocationEditorOpen(false)}
          onSave={(nextLocation) => {
            setBusinessLocation(nextLocation);
            setSelectingLocation(false);
            mapRef.current?.jumpTo({ center: nextLocation, zoom: 18 });
          }}
        />
      )}
      <Dialog open={Boolean(continuation)} onClose={() => setContinuation(null)} fullWidth maxWidth="xs">
        <DialogTitle>{continuation?.opened ? t('addPlace.transferredTitle') : t('addPlace.linkTitle')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {continuation?.opened && <Alert severity="success">{t('addPlace.transferredText')}</Alert>}
            {!continuation?.opened &&
              <Typography sx={{ fontSize: 14, textAlign: "center" }} color="text.secondary">
                {t('addPlace.linkHelp')}
              </Typography>}

            <TextField fullWidth value={continuation?.url || ''} slotProps={{ input: { readOnly: true } }} />

            <Button variant="outlined" onClick={() => navigator.clipboard.writeText(continuation?.url || '')}>
              {t('addPlace.copyLink')}
            </Button>

            <Button variant="outlined" component="a" target="_blank" rel="noreferrer" href={`https://wa.me/?text=${encodeURIComponent(continuation?.url || '')}`}>
              {t('addPlace.shareWhatsApp')}
            </Button>

            <Button variant="outlined" component="a" href={`mailto:?subject=${encodeURIComponent(t('addPlace.emailSubject'))}&body=${encodeURIComponent(continuation?.url || '')}`}>
              {t('addPlace.shareEmail')}
            </Button>

          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setContinuation(null)}>{t('placeEdit.close')}</Button></DialogActions>
      </Dialog>
    </Dialog>
  );
}
